import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { rupee, STATUS_STEPS, STATUS_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Search, Loader2, CheckCircle2, XCircle, Package } from "lucide-react";

export default function TrackOrder() {
  usePageMeta("Track My Order", "Track your Nicee order status in real time.");
  const [orderNumber, setOrderNumber] = useState("");
  const [mobile, setMobile] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const track = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const { data } = await api.get("/orders/track", { params: { order_number: orderNumber, mobile } });
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const cancelled = order?.status === "cancelled";
  const currentIdx = order ? STATUS_STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <div className="text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
          <MapPin className="h-6 w-6" />
        </span>
        <h1 className="mt-4 font-heading text-3xl font-bold text-ink">Track My Order</h1>
        <p className="mt-2 text-ink-soft">Enter your order number and mobile to see live status.</p>
      </div>

      <form onSubmit={track} className="mx-auto mt-8 max-w-md space-y-4 rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
        <div>
          <Label className="text-ink-soft">Order number</Label>
          <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="e.g. NE250805ABCDE" className="mt-1.5 rounded-xl" required data-testid="track-order-number" />
        </div>
        <div>
          <Label className="text-ink-soft">Mobile number</Label>
          <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" className="mt-1.5 rounded-xl" required data-testid="track-mobile" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="track-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-2 h-4 w-4" /> Track Order</>}
        </Button>
        {error && <p className="text-center text-sm text-destructive" data-testid="track-error">{error}</p>}
      </form>

      {order && (
        <div className="mt-8 rounded-2xl border border-border/60 bg-white p-6 shadow-ambient" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm text-ink-muted">Order</p>
              <p className="font-heading text-lg text-ink">{order.order_number}</p>
            </div>
            <span className={`rounded-full px-4 py-1.5 text-sm font-semibold ${cancelled ? "bg-destructive/10 text-destructive" : "bg-sage/10 text-sage"}`}>
              {STATUS_LABEL[order.status]}
            </span>
          </div>

          {cancelled ? (
            <div className="mt-6 flex items-center gap-3 rounded-xl bg-destructive/5 p-4 text-destructive">
              <XCircle className="h-5 w-5" /> This order was cancelled.
            </div>
          ) : (
            <div className="mt-8 space-y-6">
              {STATUS_STEPS.map((step, i) => {
                const done = i <= currentIdx;
                return (
                  <div key={step.key} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <span className={`flex h-8 w-8 items-center justify-center rounded-full ${done ? "bg-sage text-white" : "bg-cream-alt text-ink-muted"}`}>
                        {done ? <CheckCircle2 className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                      </span>
                      {i < STATUS_STEPS.length - 1 && <span className={`mt-1 h-8 w-0.5 ${i < currentIdx ? "bg-sage" : "bg-border"}`} />}
                    </div>
                    <div className="pt-1">
                      <p className={`font-medium ${done ? "text-ink" : "text-ink-muted"}`}>{step.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 border-t border-border pt-4">
            <p className="text-sm font-semibold text-ink">Items</p>
            <ul className="mt-2 space-y-1 text-sm text-ink-soft">
              {order.items.map((it, idx) => (
                <li key={idx} className="flex justify-between">
                  <span>{it.name} × {it.quantity}</span>
                  <span>{rupee(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-between font-semibold text-ink">
              <span>Total (COD)</span>
              <span>{rupee(order.total)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
